import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Box, IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import AppsRoundedIcon from '@mui/icons-material/AppsRounded';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';

const NavigationDropdown: React.FC = () => {
  // #region Constants
  const { setActivePage } = useAuth();
  // #endregion

  // #region State Variables
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const navMenuOpen = Boolean(anchorEl);
  // #endregion

  // #region Handle Functions
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  // #endregion

  return (
    <Box
      sx={{
        display: 'flex',
        padding: '8px 0',
        flexDirection: 'column',
        alignItems: 'flex-start',
        alignSelf: 'center',
      }}
    >
      <Tooltip title={"Navigation"}>
        <IconButton
          onClick={handleMenuOpen}
          sx={theme => ({
            borderRadius: '999px',
            backgroundColor: navMenuOpen ? theme.palette.action.hover : 'transparent',
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          })}
        >
          <AppsRoundedIcon />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              width: '312px',
              mt: 1,
              px: 3,
              py: 3,
              borderRadius: '28px',
              background: 'var(--background-paper-elevation-24, #FCFEFF)',
              boxShadow:
                '0 1px 18px 0 rgba(0,0,0,0.12), 0 6px 10px 0 rgba(0,0,0,0.14), 0 3px 5px -1px rgba(0,0,0,0.20)',
              '& .MuiMenuItem-root:hover': {
                backgroundColor: '#1976d20a',
              },
            },
          },
          list: {
            sx: {
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(140px, 1fr))',
              columnGap: 2,
              rowGap: 2,
              p: 0,
              justifySelf: 'center',
            },
          },
        }}
      >
        {[
          { 
            icon: <DashboardOutlinedIcon 
              sx={{
                color: theme => theme.palette.grey[600],
              }}
            />, 
            label: 'Homepage',
            activePage: 'dashboard' 
          },
          { 
            icon: <PeopleAltOutlinedIcon 
              sx={{
                color: theme => theme.palette.grey[600],
              }}
            />, 
            label: 'Supporters', 
            activePage: 'view-supporters' 
          },
          { 
            icon: <EmailOutlinedIcon 
              sx={{
                color: theme => theme.palette.grey[600],
              }}
            />, 
            label: 'Efforts', 
            activePage: 'effort-overview' 
          },
          { 
            icon: <CalendarMonthOutlinedIcon 
              sx={{
                color: theme => theme.palette.grey[600],
              }}
            />, 
            label: 'Calendar', 
            activePage: 'email-overview' 
          },
        ].map(item => (
          <MenuItem
            key={item.label}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '24px',
            }}
            onClick={() => {
              setActivePage(item.activePage);
              handleMenuClose();
            }}
          >
            {item.icon}
            <Typography 
              className='ms-subtitle2'
              mt={1}
            >
              {item.label}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default NavigationDropdown;